import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tasksAPI, inventoryAPI, customFieldsAPI, usersAPI, placesAPI } from '../lib/api';
import { formatDate, formatDateTime, getStatusColor, getStatusLabel, getPriorityColor, getPriorityLabel, compressImage, getDaysUntil } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Edit3,
  Trash2,
  Plus,
  Camera,
  Upload,
  X,
  CheckCircle2,
  Circle,
  MapPin,
  Calendar,
  Clock,
  Image as ImageIcon,
  Milestone,
  Loader2,
  Save,
  AlertTriangle,
  Package,
  Wrench,
  MessageSquare,
  Send,
  DollarSign,
  FileText,
  Download,
  File,
  History,
  Play,
  ClipboardList,
  User
} from 'lucide-react';
import Layout from '../components/Layout';
import { useBranding } from '../context/BrandingContext';
import { useAuth } from '../context/AuthContext';
import TranslatableText, { TranslatableSpan } from '../components/TranslatableText';

const TaskDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { user } = useAuth();
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);
  
  // Check if user is technician
  const isTechnician = user?.role === 'technician';
  
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [newMilestone, setNewMilestone] = useState('');
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState('');
  
  // Parts management state
  const [inventoryItems, setInventoryItems] = useState([]);
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState('');
  const [partQuantity, setPartQuantity] = useState(1);
  const [addingPart, setAddingPart] = useState(false);
  
  // Notes state
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Documents state
  const documentInputRef = useRef(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Activity Log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Custom Fields state
  const [customFields, setCustomFields] = useState([]);
  
  // Users state for assignment
  const [availableUsers, setAvailableUsers] = useState([]);
  
  // Places state for location
  const [places, setPlaces] = useState([]);

  const fetchPlaces = async () => {
    try {
      const response = await placesAPI.getAll();
      setPlaces(response.data);
    } catch (err) {
      console.error('Failed to fetch places:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      // Filter to show technicians and supervisors for assignment
      setAvailableUsers(response.data.filter(u => u.role === 'technician' || u.role === 'supervisor'));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data);
    } catch (err) {
      console.error('Failed to fetch custom fields:', err);
    }
  };

  const fetchTask = useCallback(async () => {
    try {
      const response = await tasksAPI.getOne(id);
      setTask(response.data);
      setEditForm({
        title: response.data.title,
        description: response.data.description,
        location: response.data.location,
        place_id: response.data.place_id || '',
        priority: response.data.priority,
        deadline: response.data.deadline?.split('T')[0] || '',
        labor_hours: response.data.labor_hours || 0,
        hourly_rate: response.data.hourly_rate || 50,
        assigned_to: response.data.assigned_to || '',
      });
    } catch (err) {
      console.error('Failed to fetch task:', err);
      toast.error('Failed to load work order');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchInventory = async () => {
    try {
      const response = await inventoryAPI.getAll();
      setInventoryItems(response.data);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
    }
  };

  const fetchActivityLogs = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const response = await tasksAPI.getActivities(id);
      setActivityLogs(response.data);
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoadingActivity(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTask();
    fetchInventory();
    fetchActivityLogs();
    fetchCustomFields();
    fetchUsers();
    fetchPlaces();
  }, [id, fetchTask, fetchActivityLogs]);

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await tasksAPI.update(id, { status: newStatus });
      setTask(response.data);
      toast.success(`Status updated to ${getStatusLabel(newStatus)}`);
      fetchActivityLogs(); // Refresh activity logs
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await tasksAPI.update(id, editForm);
      setTask(response.data);
      setEditing(false);
      toast.success('Work order updated successfully');
    } catch (err) {
      toast.error('Failed to update work order');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await tasksAPI.delete(id);
      toast.success('Work order deleted');
      navigate('/tasks');
    } catch (err) {
      toast.error('Failed to delete work order');
    }
  };

  const handleAddMilestone = async () => {
    if (!newMilestone.trim()) return;
    
    try {
      const response = await tasksAPI.addMilestone(id, { title: newMilestone });
      setTask(response.data);
      setNewMilestone('');
      toast.success('Milestone added');
      fetchActivityLogs();
    } catch (err) {
      toast.error('Failed to add milestone');
    }
  };

  const handleToggleMilestone = async (milestoneId, completed) => {
    try {
      const response = await tasksAPI.updateMilestone(id, milestoneId, { completed: !completed });
      setTask(response.data);
      fetchActivityLogs();
    } catch (err) {
      toast.error('Failed to update milestone');
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    try {
      await tasksAPI.deleteMilestone(id, milestoneId);
      setTask(prev => ({
        ...prev,
        milestones: prev.milestones.filter(m => m.id !== milestoneId)
      }));
      toast.success('Milestone deleted');
    } catch (err) {
      toast.error('Failed to delete milestone');
    }
  };

  const handleImageUpload = async (e, imageType = 'before') => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (imageType === 'before') {
      setUploadingBefore(true);
    } else {
      setUploadingAfter(true);
    }
    
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        await tasksAPI.addImage(id, { 
          data: compressed,
          caption: file.name,
          image_type: imageType
        });
      }
      await fetchTask();
      toast.success(`${files.length} ${imageType} photo${files.length > 1 ? 's' : ''} uploaded`);
      fetchActivityLogs();
    } catch (err) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingBefore(false);
      setUploadingAfter(false);
    }
  };

  const handleDeleteImage = async (imageId, imageType = 'before') => {
    try {
      await tasksAPI.deleteImage(id, imageId, imageType);
      if (imageType === 'before') {
        setTask(prev => ({
          ...prev,
          before_images: prev.before_images.filter(img => img.id !== imageId)
        }));
      } else {
        setTask(prev => ({
          ...prev,
          after_images: prev.after_images.filter(img => img.id !== imageId)
        }));
      }
      fetchActivityLogs();
      setSelectedImage(null);
      toast.success('Image deleted');
    } catch (err) {
      toast.error('Failed to delete image');
    }
  };

  const handleEditCaption = (image, imageType) => {
    setEditingCaption({ id: image.id, type: imageType });
    setCaptionText(image.caption || '');
  };

  const handleSaveCaption = async () => {
    if (!editingCaption) return;
    
    try {
      await tasksAPI.updateImageCaption(id, editingCaption.id, captionText, editingCaption.type);
      
      if (editingCaption.type === 'before') {
        setTask(prev => ({
          ...prev,
          before_images: prev.before_images.map(img => 
            img.id === editingCaption.id ? { ...img, caption: captionText } : img
          )
        }));
      } else {
        setTask(prev => ({
          ...prev,
          after_images: prev.after_images.map(img => 
            img.id === editingCaption.id ? { ...img, caption: captionText } : img
          )
        }));
      }
      
      setEditingCaption(null);
      setCaptionText('');
      toast.success('Caption updated');
    } catch (err) {
      toast.error('Failed to update caption');
    }
  };

  // Parts management functions
  const handleAddPart = async () => {
    if (!selectedPart || partQuantity < 1) {
      toast.error('Please select a part and quantity');
      return;
    }
    
    setAddingPart(true);
    try {
      const response = await tasksAPI.addPart(id, {
        item_id: selectedPart,
        quantity: partQuantity
      });
      
      setTask(prev => ({
        ...prev,
        parts_used: [...(prev.parts_used || []), response.data]
      }));
      
      // Update local inventory count
      setInventoryItems(prev => prev.map(item => 
        item.id === selectedPart 
          ? { ...item, quantity: item.quantity - partQuantity }
          : item
      ));
      
      setPartsDialogOpen(false);
      setSelectedPart('');
      setPartQuantity(1);
      toast.success('Part added to work order');
      fetchActivityLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add part');
    } finally {
      setAddingPart(false);
    }
  };

  const handleRemovePart = async (partId) => {
    try {
      await tasksAPI.removePart(id, partId);
      
      const removedPart = task.parts_used?.find(p => p.id === partId);
      
      setTask(prev => ({
        ...prev,
        parts_used: prev.parts_used.filter(p => p.id !== partId)
      }));
      
      // Return quantity to local inventory
      if (removedPart) {
        setInventoryItems(prev => prev.map(item => 
          item.id === removedPart.item_id 
            ? { ...item, quantity: item.quantity + removedPart.quantity }
            : item
        ));
      }
      
      toast.success('Part removed and returned to inventory');
    } catch (err) {
      toast.error('Failed to remove part');
    }
  };

  const getTotalPartsCost = () => {
    return (task?.parts_used || []).reduce((total, part) => 
      total + (part.quantity * part.cost_per_unit), 0
    ).toFixed(2);
  };

  // Notes functions
  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setAddingNote(true);
    try {
      const response = await tasksAPI.addNote(id, { text: noteText });
      setTask(prev => ({
        ...prev,
        notes: [...(prev.notes || []), response.data]
      }));
      setNoteText('');
      toast.success('Note added');
      fetchActivityLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await tasksAPI.deleteNote(id, noteId);
      setTask(prev => ({
        ...prev,
        notes: prev.notes.filter(n => n.id !== noteId)
      }));
      toast.success('Note deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  // Document handling
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    setUploadingDoc(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        const response = await tasksAPI.addDocument(id, {
          filename: file.name,
          file_type: file.type,
          file_size: file.size,
          data: base64
        });
        
        setTask(prev => ({
          ...prev,
          documents: [...(prev.documents || []), response.data]
        }));
        toast.success('Document uploaded');
        fetchActivityLogs();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await tasksAPI.deleteDocument(id, docId);
      setTask(prev => ({
        ...prev,
        documents: (prev.documents || []).filter(d => d.id !== docId)
      }));
      toast.success('Document deleted');
      fetchActivityLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const downloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = `data:${doc.file_type};base64,${doc.data}`;
    link.download = doc.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  if (!task) return null;

  const daysUntil = getDaysUntil(task.deadline);
  const isOverdue = daysUntil !== null && daysUntil < 0 && task.status !== 'completed';
  const completedMilestones = task.milestones?.filter(m => m.completed).length || 0;
  const totalMilestones = task.milestones?.length || 0;
  const progress = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="task-detail-page">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/tasks')}
          className="btn-ghost flex items-center gap-2 -ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Work Orders
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            {editing ? (
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                className="input-industrial text-2xl font-bold mb-2"
                data-testid="edit-title-input"
              />
            ) : (
              <>
                {task.work_order_number && (
                  <span className="inline-block text-cyan-400 font-mono text-sm bg-cyan-500/10 px-3 py-1 rounded mb-2" data-testid="work-order-number">
                    {task.work_order_number}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-2">
                  <span className={`priority-indicator ${task.priority}`}></span>
                  <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
                    <TranslatableText text={task.title} as="span" />
                  </h1>
                </div>
              </>
            )}
            
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className={`status-badge text-sm ${getStatusColor(task.status)}`}>
                {getStatusLabel(task.status)}
              </span>
              <span className={`px-3 py-1 rounded-sm text-xs font-mono uppercase ${getPriorityColor(task.priority)} border`}>
                {getPriorityLabel(task.priority)}
              </span>
              {isOverdue && (
                <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/50 rounded-sm text-xs font-mono uppercase">
                  <AlertTriangle className="w-3 h-3" />
                  Overdue
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button 
                  onClick={() => setEditing(false)} 
                  className="btn-secondary"
                  data-testid="cancel-edit-btn"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveEdit} 
                  disabled={saving}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-edit-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setEditing(true)} 
                  className="btn-secondary flex items-center gap-2 h-10"
                  data-testid="edit-btn"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 h-10 w-10 p-0 flex items-center justify-center" data-testid="delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-slate-900 border-slate-700">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Delete Work Order</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">
                        Are you sure you want to delete this work order? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </div>

        {/* Status Selector */}
        <div className="card-industrial">
          <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
            Update Status
          </h3>
          
          {/* Show message if technician can't change completed status */}
          {task.status === 'completed' && isTechnician && (
            <div className="mb-4 p-3 bg-slate-700/50 border border-slate-600 rounded-sm">
              <p className="text-slate-400 text-sm">
                This work order is completed. Only Admin or Supervisor can change its status.
              </p>
            </div>
          )}
          
          {/* Start Work Button - Show prominently when task is reported */}
          {task.status === 'reported' && (
            <div className="mb-4">
              <Button
                onClick={() => handleStatusChange('in_progress')}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="start-work-btn"
              >
                <Play className="w-5 h-5" />
                Start Work
              </Button>
            </div>
          )}
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {['reported', 'assigned', 'in_progress', 'completed'].map((status) => {
              // Disable status buttons for technicians if task is completed
              const isDisabled = task.status === 'completed' && isTechnician && status !== 'completed';
              const isCurrentStatus = task.status === status;
              
              return (
                <Button
                  key={status}
                  onClick={() => !isDisabled && handleStatusChange(status)}
                  disabled={isDisabled}
                  className={`${
                    isCurrentStatus 
                      ? 'bg-amber-500 text-white border-amber-500' 
                      : isDisabled
                        ? 'bg-slate-800 text-slate-600 border-slate-700 cursor-not-allowed'
                        : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                  } border rounded-sm px-3 py-2 font-mono text-xs sm:text-sm uppercase transition-colors h-10 flex items-center justify-center`}
                  data-testid={`status-btn-${status}`}
                >
                  {getStatusLabel(status)}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column - Details */}
          <div className="space-y-6">
            {/* Description */}
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-3">
                Description
              </h3>
              {editing ? (
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-industrial min-h-[100px]"
                  data-testid="edit-description-input"
                />
              ) : (
                <div className="text-zinc-300 whitespace-pre-wrap">
                  <TranslatableText text={task.description} as="p" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="card-industrial">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4">
                Details
              </h3>
              <div className="space-y-4">
                {/* Location */}
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Location</p>
                    {editing ? (
                      <Select 
                        value={editForm.place_id || 'none'} 
                        onValueChange={(value) => {
                          const selectedPlace = places.find(p => p.id === value);
                          setEditForm(prev => ({ 
                            ...prev, 
                            place_id: value === 'none' ? '' : value,
                            location: selectedPlace ? `${selectedPlace.building_name} / ${selectedPlace.floor_name} / ${selectedPlace.name}` : ''
                          }));
                        }}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="edit-location-select">
                          <SelectValue placeholder="Select location..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700 max-h-60">
                          <SelectItem value="none">No location</SelectItem>
                          {places.map((place) => (
                            <SelectItem key={place.id} value={place.id}>
                              <span className="text-slate-400 text-xs mr-2">{place.building_name} / {place.floor_name}</span>
                              {place.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-white">
                        {task.place_name ? (
                          <>
                            {task.building_name && <span className="text-slate-400">{task.building_name}</span>}
                            {task.floor_name && <span className="text-slate-400"> / {task.floor_name}</span>}
                            {(task.building_name || task.floor_name) && <span className="text-slate-400"> / </span>}
                            {task.place_name}
                          </>
                        ) : 'Not specified'}
                      </p>
                    )}
                  </div>
                </div>

                {/* Deadline */}
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Deadline</p>
                    {editing ? (
                      <Input
                        type="date"
                        value={editForm.deadline}
                        onChange={(e) => setEditForm(prev => ({ ...prev, deadline: e.target.value }))}
                        className="input-industrial"
                        data-testid="edit-deadline-input"
                      />
                    ) : (
                      <p className={`${isOverdue ? 'text-red-400' : 'text-white'}`}>
                        {task.deadline ? formatDate(task.deadline) : 'Not set'}
                        {daysUntil !== null && task.status !== 'completed' && (
                          <span className={`ml-2 text-sm ${isOverdue ? 'text-red-400' : 'text-slate-400'}`}>
                            ({isOverdue ? 'Overdue' : daysUntil === 0 ? 'Today' : `${daysUntil} days left`})
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Priority */}
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Priority</p>
                    {editing ? (
                      <Select 
                        value={editForm.priority} 
                        onValueChange={(value) => setEditForm(prev => ({ ...prev, priority: value }))}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="edit-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`px-3 py-1 rounded-sm text-xs font-mono uppercase ${getPriorityColor(task.priority)} border inline-block`}>
                        {getPriorityLabel(task.priority)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Assigned To */}
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Assigned To</p>
                    {editing ? (
                      <Select 
                        value={editForm.assigned_to || 'unassigned'} 
                        onValueChange={(value) => setEditForm(prev => ({ ...prev, assigned_to: value === 'unassigned' ? '' : value }))}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="edit-assigned-select">
                          <SelectValue placeholder="Select technician..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {availableUsers.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name || u.full_name || u.email} ({u.role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-white">
                        {task.assigned_to_name ? (
                          <span className="text-amber-400">{task.assigned_to_name}</span>
                        ) : (
                          <span className="text-slate-500">Unassigned</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Created */}
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Created</p>
                    <p className="text-white">{formatDateTime(task.created_at)}</p>
                    {task.created_by_name && (
                      <p className="text-slate-400 text-sm mt-1">
                        by <span className="text-amber-400">{task.created_by_name}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Custom Fields Section */}
            {(branding?.feature_custom_fields !== false) && customFields.length > 0 && Object.keys(task.custom_field_values || {}).length > 0 && (
              <div className="card-industrial">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-cyan-400" />
                  Custom Fields
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customFields.map((field) => {
                    const value = task.custom_field_values?.[field.id];
                    if (!value) return null;
                    
                    return (
                      <div key={field.id} className="flex items-start gap-3">
                        <div className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0">
                          {field.field_type === 'text' && <FileText className="w-5 h-5" />}
                          {field.field_type === 'number' && <span className="text-sm font-mono">#</span>}
                          {field.field_type === 'dropdown' && <ClipboardList className="w-5 h-5" />}
                          {field.field_type === 'date' && <Calendar className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">{field.name}</p>
                          <p className="text-white">
                            {field.field_type === 'date' 
                              ? formatDate(value)
                              : field.field_type === 'text' 
                                ? <TranslatableSpan text={value} />
                                : value}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Milestones & Images */}
          <div className="space-y-6">
            {/* Milestones */}
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <Milestone className="w-5 h-5 text-amber-500" />
                  Milestones
                </h3>
                <span className="text-sm text-slate-400">{completedMilestones}/{totalMilestones}</span>
              </div>

              {/* Progress Bar */}
              {totalMilestones > 0 && (
                <div className="h-2 bg-slate-700 rounded-full mb-4 overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}

              {/* Milestone List */}
              <div className="space-y-2 mb-4">
                {task.milestones?.map((milestone) => (
                  <div 
                    key={milestone.id}
                    className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-sm group"
                    data-testid={`milestone-${milestone.id}`}
                  >
                    <Checkbox
                      checked={milestone.completed}
                      onCheckedChange={() => handleToggleMilestone(milestone.id, milestone.completed)}
                      className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                      data-testid={`milestone-checkbox-${milestone.id}`}
                    />
                    <span className={`flex-1 ${milestone.completed ? 'text-slate-400 line-through' : 'text-white'}`}>
                      <TranslatableSpan text={milestone.title} />
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteMilestone(milestone.id)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-400 transition-opacity p-1 h-auto"
                      data-testid={`delete-milestone-${milestone.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Add Milestone */}
              <div className="flex gap-2">
                <Input
                  value={newMilestone}
                  onChange={(e) => setNewMilestone(e.target.value)}
                  placeholder="Add milestone..."
                  className="input-industrial flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddMilestone()}
                  data-testid="new-milestone-input"
                />
                <Button 
                  onClick={handleAddMilestone}
                  className="btn-primary px-4"
                  data-testid="add-milestone-btn"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Parts Used - Only show for in_progress tasks */}
            {task.status === 'in_progress' && (
              <div className="card-industrial border-amber-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-amber-400" />
                    Parts Used
                  </h3>
                  <Dialog open={partsDialogOpen} onOpenChange={setPartsDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="btn-primary text-sm" data-testid="add-part-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Part
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                          <Package className="w-5 h-5 text-amber-400" />
                          Add Part from Inventory
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Select Part</label>
                          <Select value={selectedPart} onValueChange={setSelectedPart}>
                            <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="part-select">
                              <SelectValue placeholder="Choose a part..." />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-700 max-h-[300px]">
                              {inventoryItems.filter(item => item.quantity > 0).map((item) => (
                                <SelectItem key={item.id} value={item.id}>
                                  <div className="flex justify-between items-center gap-4">
                                    <span>{item.name}</span>
                                    <span className="text-slate-400 text-xs">({item.quantity} {item.unit} available)</span>
                                  </div>
                                </SelectItem>
                              ))}
                              {inventoryItems.filter(item => item.quantity > 0).length === 0 && (
                                <div className="p-3 text-slate-500 text-center">No parts in stock</div>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Quantity</label>
                          <Input
                            type="number"
                            min="1"
                            value={partQuantity}
                            onChange={(e) => setPartQuantity(parseInt(e.target.value) || 1)}
                            className="input-industrial"
                            data-testid="part-quantity-input"
                          />
                        </div>
                        <div className="flex gap-3 pt-2">
                          <Button
                            onClick={() => setPartsDialogOpen(false)}
                            className="btn-secondary flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleAddPart}
                            disabled={addingPart || !selectedPart}
                            className="btn-primary flex-1 flex items-center justify-center gap-2"
                            data-testid="confirm-add-part-btn"
                          >
                            {addingPart ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Add Part
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Parts List */}
                {task.parts_used?.length > 0 ? (
                  <div className="space-y-2">
                    {task.parts_used.map((part) => (
                      <div 
                        key={part.id} 
                        className="flex items-center justify-between p-3 bg-slate-800/50 rounded-sm border border-slate-700"
                        data-testid={`part-${part.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Package className="w-5 h-5 text-amber-400" />
                          <div>
                            <p className="text-white font-medium">{part.item_name}</p>
                            <p className="text-slate-400 text-sm">
                              {part.quantity} × ${part.cost_per_unit?.toFixed(2) || '0.00'} = ${(part.quantity * (part.cost_per_unit || 0)).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 p-2"
                              data-testid={`remove-part-${part.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-slate-900 border-slate-700">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Remove Part</AlertDialogTitle>
                              <AlertDialogDescription className="text-slate-400">
                                Remove {part.item_name} from this task? The quantity will be returned to inventory.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleRemovePart(part.id)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                    
                    {/* Total Cost */}
                    <div className="flex justify-between items-center pt-3 mt-3 border-t border-slate-700">
                      <span className="text-slate-400">Total Parts Cost</span>
                      <span className="text-amber-400 font-bold text-lg">${getTotalPartsCost()}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No parts added yet</p>
                )}
              </div>
            )}

            {/* Show parts summary for completed tasks */}
            {task.status === 'completed' && task.parts_used?.length > 0 && (
              <div className="card-industrial border-green-500/30">
                <div className="flex items-center gap-2 mb-4">
                  <Wrench className="w-5 h-5 text-green-400" />
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
                    Parts Used
                  </h3>
                </div>
                <div className="space-y-2">
                  {task.parts_used.map((part) => (
                    <div 
                      key={part.id} 
                      className="flex items-center justify-between p-2 bg-slate-800/50 rounded-sm"
                    >
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-400" />
                        <span className="text-white">{part.item_name}</span>
                        <span className="text-slate-400">×{part.quantity}</span>
                      </div>
                      <span className="text-slate-400">${(part.quantity * (part.cost_per_unit || 0)).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-700">
                    <span className="text-slate-400">Total</span>
                    <span className="text-green-400 font-bold">${getTotalPartsCost()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Work Notes - Show for in_progress tasks */}
            {task.status === 'in_progress' && (
              <div className="card-industrial border-purple-500/30">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                    Work Notes
                  </h3>
                  <span className="text-sm text-slate-400">{task.notes?.length || 0} notes</span>
                </div>
                
                {/* Add Note Input */}
                <div className="flex gap-2 mb-4">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a work note... (observations, progress updates, etc.)"
                    className="input-industrial min-h-[80px] flex-1"
                    data-testid="note-input"
                  />
                </div>
                <div className="flex justify-end mb-4">
                  <Button
                    onClick={handleAddNote}
                    disabled={addingNote || !noteText.trim()}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-note-btn"
                  >
                    {addingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Add Note
                  </Button>
                </div>

                {/* Notes List */}
                {task.notes?.length > 0 ? (
                  <div className="space-y-3">
                    {task.notes.slice().reverse().map((note) => (
                      <div 
                        key={note.id} 
                        className="p-4 bg-slate-800/50 rounded-sm border border-slate-700"
                        data-testid={`note-${note.id}`}
                      >
                        <div className="flex justify-between items-start gap-3">
                          <div className="flex-1">
                            <div className="text-white whitespace-pre-wrap">
                              <TranslatableText text={note.text} as="p" showToggle={false} />
                            </div>
                            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                              <span className="font-medium text-purple-400">{note.author_name}</span>
                              <span>•</span>
                              <span>{formatDateTime(note.created_at)}</span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleDeleteNote(note.id)}
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 p-2"
                            data-testid={`delete-note-${note.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No notes yet. Add observations or progress updates.</p>
                )}
              </div>
            )}

            {/* Show notes for completed tasks (read-only) */}
            {task.status === 'completed' && task.notes?.length > 0 && (
              <div className="card-industrial border-slate-600">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-slate-400" />
                  <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white">
                    Work Notes
                  </h3>
                  <span className="text-sm text-slate-400 ml-auto">{task.notes.length} notes</span>
                </div>
                <div className="space-y-3">
                  {task.notes.slice().reverse().map((note) => (
                    <div 
                      key={note.id} 
                      className="p-3 bg-slate-800/30 rounded-sm border border-slate-700"
                    >
                      <div className="text-slate-300 whitespace-pre-wrap">
                        <TranslatableText text={note.text} as="p" showToggle={false} />
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                        <span>{note.author_name}</span>
                        <span>•</span>
                        <span>{formatDateTime(note.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cost Tracking Section - Hidden from Technicians */}
            {(branding?.feature_cost_tracking !== false) && !isTechnician && (
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Cost Tracking
                </h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-700/50 p-3 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase mb-1">Labor Hours</p>
                  <p className="text-2xl font-bold text-white">{task.labor_hours || 0}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase mb-1">Labor Cost</p>
                  <p className="text-2xl font-bold text-blue-400">${(task.labor_cost || 0).toFixed(2)}</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-sm">
                  <p className="text-slate-400 text-xs uppercase mb-1">Parts Cost</p>
                  <p className="text-2xl font-bold text-amber-400">${(task.parts_cost || 0).toFixed(2)}</p>
                </div>
                <div className="bg-green-500/10 border border-green-500/30 p-3 rounded-sm">
                  <p className="text-green-400 text-xs uppercase mb-1">Total Cost</p>
                  <p className="text-2xl font-bold text-green-400">${(task.total_cost || 0).toFixed(2)}</p>
                </div>
              </div>

              {/* Labor Hours Edit */}
              {(task.status === 'in_progress' || task.status === 'completed') && (
                <div className="border-t border-slate-700 pt-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="text-slate-400 text-xs uppercase tracking-wider mb-2 block">Labor Hours</label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        value={editForm.labor_hours}
                        onChange={(e) => setEditForm({ ...editForm, labor_hours: parseFloat(e.target.value) || 0 })}
                        className="input-industrial"
                        data-testid="labor-hours-input"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-slate-400 text-xs uppercase tracking-wider mb-2 block">Hourly Rate ($)</label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={editForm.hourly_rate}
                        onChange={(e) => setEditForm({ ...editForm, hourly_rate: parseFloat(e.target.value) || 0 })}
                        className="input-industrial"
                        data-testid="hourly-rate-input"
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={() => {
                          tasksAPI.update(id, { 
                            labor_hours: editForm.labor_hours, 
                            hourly_rate: editForm.hourly_rate 
                          }).then(res => {
                            setTask(res.data);
                            toast.success('Labor hours updated');
                          }).catch(() => toast.error('Failed to update'));
                        }}
                        className="btn-primary"
                        data-testid="save-labor-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Documents Section */}
            {(branding?.feature_documents !== false) && (
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Documents & Attachments
                </h3>
                <span className="text-sm text-slate-400">{task.documents?.length || 0} files</span>
              </div>
              
              {/* Document List */}
              {task.documents?.length > 0 && (
                <div className="space-y-2 mb-4">
                  {task.documents.map((doc) => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-slate-700/50 rounded-sm border border-slate-600"
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <File className="w-8 h-8 text-purple-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{doc.filename}</p>
                          <p className="text-slate-400 text-xs">
                            {formatFileSize(doc.file_size)} • Uploaded by {doc.uploaded_by_name || 'Unknown'} • {formatDateTime(doc.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadDocument(doc)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(doc.id)}
                          className="text-slate-400 hover:text-red-400"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <div 
                onClick={() => documentInputRef.current?.click()}
                className="flex items-center justify-center p-6 border-2 border-dashed border-slate-600 rounded-sm cursor-pointer hover:border-purple-500/50 transition-colors"
                data-testid="document-upload-area"
              >
                <input
                  ref={documentInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                  onChange={handleDocumentUpload}
                  className="hidden"
                  data-testid="document-input"
                />
                {uploadingDoc ? (
                  <Loader2 className="w-6 h-6 animate-spin text-purple-400 mr-2" />
                ) : (
                  <Upload className="w-6 h-6 text-purple-400 mr-2" />
                )}
                <span className="text-slate-400">
                  {uploadingDoc ? 'Uploading...' : 'Upload PDF, Word, Excel, or Image (Max 10MB)'}
                </span>
              </div>
            </div>
            )}

            {/* Before Photos */}
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-blue-400" />
                  Before Photos
                </h3>
                <span className="text-sm text-slate-400">{task.before_images?.length || 0} photos</span>
              </div>
              <p className="text-slate-400 text-sm mb-4">Document the problem before work begins</p>

              {/* Before Image Grid */}
              {task.before_images?.length > 0 && (
                <div className="image-grid mb-4">
                  {task.before_images.map((image) => (
                    <Dialog key={image.id}>
                      <DialogTrigger asChild>
                        <div 
                          className="image-grid-item cursor-pointer border border-blue-500/30 hover:border-blue-500/50 transition-colors relative"
                          data-testid={`before-image-${image.id}`}
                        >
                          <img src={image.data} alt={image.caption || 'Before photo'} />
                          <span className="absolute top-1 left-1 bg-blue-500/80 text-white text-xs px-2 py-0.5 rounded-sm">BEFORE</span>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl">
                        <DialogHeader>
                          <DialogTitle className="text-white flex items-center gap-2">
                            <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-sm">BEFORE</span>
                            {editingCaption?.id === image.id ? 'Edit Caption' : (image.caption || 'Photo')}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="relative">
                          <img 
                            src={image.data} 
                            alt={image.caption || 'Before photo'} 
                            className="w-full rounded-sm"
                          />
                          
                          {/* Caption Edit Section */}
                          {editingCaption?.id === image.id ? (
                            <div className="mt-4 space-y-3">
                              <Input
                                value={captionText}
                                onChange={(e) => setCaptionText(e.target.value)}
                                placeholder="Enter caption..."
                                className="input-industrial"
                                data-testid="edit-caption-input"
                              />
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => { setEditingCaption(null); setCaptionText(''); }}
                                  className="btn-secondary flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleSaveCaption}
                                  className="btn-primary flex-1"
                                  data-testid="save-caption-btn"
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  Save Caption
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {image.caption && (
                                <p className="text-slate-300 mt-3 p-2 bg-slate-800/50 rounded-sm">
                                  <TranslatableSpan text={image.caption} />
                                </p>
                              )}
                              <div className="flex justify-between items-center mt-4">
                                <span className="text-slate-400 text-sm">{formatDateTime(image.uploaded_at)}</span>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleEditCaption(image, 'before')}
                                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50"
                                    data-testid={`edit-before-image-${image.id}`}
                                  >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteImage(image.id, 'before')}
                                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50"
                                    data-testid={`delete-before-image-${image.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              )}

              {/* Before Upload Area */}
              <div 
                className="image-upload-area p-6 text-center cursor-pointer border-blue-500/30 hover:border-blue-500/50"
                onClick={() => beforeInputRef.current?.click()}
                data-testid="before-image-upload-area"
              >
                <input
                  ref={beforeInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, 'before')}
                  className="hidden"
                  data-testid="before-image-input"
                />
                {uploadingBefore ? (
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-2" />
                ) : (
                  <Camera className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                )}
                <p className="text-slate-400">
                  {uploadingBefore ? 'Uploading...' : 'Add before photos'}
                </p>
                <p className="text-slate-500 text-sm mt-1">Show the problem</p>
              </div>
            </div>

            {/* After Photos */}
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-green-400" />
                  After Photos
                </h3>
                <span className="text-sm text-slate-400">{task.after_images?.length || 0} photos</span>
              </div>
              <p className="text-slate-400 text-sm mb-4">Document the completed work</p>

              {/* After Image Grid */}
              {task.after_images?.length > 0 && (
                <div className="image-grid mb-4">
                  {task.after_images.map((image) => (
                    <Dialog key={image.id}>
                      <DialogTrigger asChild>
                        <div 
                          className="image-grid-item cursor-pointer border border-green-500/30 hover:border-green-500/50 transition-colors relative"
                          data-testid={`after-image-${image.id}`}
                        >
                          <img src={image.data} alt={image.caption || 'After photo'} />
                          <span className="absolute top-1 left-1 bg-green-500/80 text-white text-xs px-2 py-0.5 rounded-sm">AFTER</span>
                        </div>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl">
                        <DialogHeader>
                          <DialogTitle className="text-white flex items-center gap-2">
                            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-sm">AFTER</span>
                            {editingCaption?.id === image.id ? 'Edit Caption' : (image.caption || 'Photo')}
                          </DialogTitle>
                        </DialogHeader>
                        <div className="relative">
                          <img 
                            src={image.data} 
                            alt={image.caption || 'After photo'} 
                            className="w-full rounded-sm"
                          />
                          
                          {/* Caption Edit Section */}
                          {editingCaption?.id === image.id ? (
                            <div className="mt-4 space-y-3">
                              <Input
                                value={captionText}
                                onChange={(e) => setCaptionText(e.target.value)}
                                placeholder="Enter caption..."
                                className="input-industrial"
                                data-testid="edit-after-caption-input"
                              />
                              <div className="flex gap-2">
                                <Button
                                  onClick={() => { setEditingCaption(null); setCaptionText(''); }}
                                  className="btn-secondary flex-1"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleSaveCaption}
                                  className="btn-primary flex-1"
                                  data-testid="save-after-caption-btn"
                                >
                                  <Save className="w-4 h-4 mr-2" />
                                  Save Caption
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {image.caption && (
                                <p className="text-slate-300 mt-3 p-2 bg-slate-800/50 rounded-sm">
                                  <TranslatableSpan text={image.caption} />
                                </p>
                              )}
                              <div className="flex justify-between items-center mt-4">
                                <span className="text-slate-400 text-sm">{formatDateTime(image.uploaded_at)}</span>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => handleEditCaption(image, 'after')}
                                    className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50"
                                    data-testid={`edit-after-image-${image.id}`}
                                  >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    Edit
                                  </Button>
                                  <Button
                                    onClick={() => handleDeleteImage(image.id, 'after')}
                                    className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50"
                                    data-testid={`delete-after-image-${image.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              )}

              {/* After Upload Area */}
              <div 
                className="image-upload-area p-6 text-center cursor-pointer border-green-500/30 hover:border-green-500/50"
                onClick={() => afterInputRef.current?.click()}
                data-testid="after-image-upload-area"
              >
                <input
                  ref={afterInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, 'after')}
                  className="hidden"
                  data-testid="after-image-input"
                />
                {uploadingAfter ? (
                  <Loader2 className="w-8 h-8 animate-spin text-green-400 mx-auto mb-2" />
                ) : (
                  <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-2" />
                )}
                <p className="text-slate-400">
                  {uploadingAfter ? 'Uploading...' : 'Add after photos'}
                </p>
                <p className="text-slate-500 text-sm mt-1">Show the completed work</p>
              </div>
            </div>

            {/* Activity Log Section */}
            <div className="card-industrial">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-cyan-400" />
                  Activity Log
                </h3>
                <span className="text-sm text-slate-400">{activityLogs.length} entries</span>
              </div>
              
              {loadingActivity ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                </div>
              ) : activityLogs.length > 0 ? (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {activityLogs.map((log, index) => (
                    <div 
                      key={log.id}
                      className="flex gap-3 relative"
                      data-testid={`activity-log-${log.id}`}
                    >
                      {/* Timeline line */}
                      {index < activityLogs.length - 1 && (
                        <div className="absolute left-[11px] top-6 bottom-0 w-0.5 bg-slate-700" />
                      )}
                      
                      {/* Icon */}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${getActivityIconStyle(log.action)}`}>
                        {getActivityIcon(log.action)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-white text-sm font-medium">
                              {getActivityLabel(log.action)}
                            </p>
                            {log.details && (
                              <p className="text-slate-400 text-xs mt-0.5">
                                <TranslatableSpan text={log.details} />
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span className="text-cyan-400">{log.user_name}</span>
                          <span>•</span>
                          <span>{formatDateTime(log.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-6">No activity recorded yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Helper functions for Activity Log
const getActivityIcon = (action) => {
  const iconSize = "w-3 h-3";
  switch (action) {
    case 'task_created':
      return <Plus className={iconSize} />;
    case 'status_changed':
      return <CheckCircle2 className={iconSize} />;
    case 'assigned':
      return <Wrench className={iconSize} />;
    case 'priority_changed':
      return <AlertTriangle className={iconSize} />;
    case 'photo_added':
    case 'photo_deleted':
      return <Camera className={iconSize} />;
    case 'part_added':
      return <Package className={iconSize} />;
    case 'note_added':
      return <MessageSquare className={iconSize} />;
    case 'document_added':
    case 'document_deleted':
      return <FileText className={iconSize} />;
    case 'milestone_added':
    case 'milestone_completed':
      return <Milestone className={iconSize} />;
    default:
      return <History className={iconSize} />;
  }
};

const getActivityIconStyle = (action) => {
  switch (action) {
    case 'task_created':
      return 'bg-green-500/20 text-green-400';
    case 'status_changed':
      return 'bg-amber-500/20 text-amber-400';
    case 'assigned':
      return 'bg-blue-500/20 text-blue-400';
    case 'priority_changed':
      return 'bg-orange-500/20 text-orange-400';
    case 'photo_added':
      return 'bg-cyan-500/20 text-cyan-400';
    case 'photo_deleted':
      return 'bg-red-500/20 text-red-400';
    case 'part_added':
      return 'bg-purple-500/20 text-purple-400';
    case 'note_added':
      return 'bg-pink-500/20 text-pink-400';
    case 'document_added':
      return 'bg-indigo-500/20 text-indigo-400';
    case 'document_deleted':
      return 'bg-red-500/20 text-red-400';
    case 'milestone_added':
      return 'bg-teal-500/20 text-teal-400';
    case 'milestone_completed':
      return 'bg-green-500/20 text-green-400';
    default:
      return 'bg-slate-700 text-slate-400';
  }
};

const getActivityLabel = (action) => {
  const labels = {
    'task_created': 'Work order created',
    'task_updated': 'Work order updated',
    'task_deleted': 'Work order deleted',
    'status_changed': 'Status changed',
    'assigned': 'Assigned to technician',
    'priority_changed': 'Priority changed',
    'photo_added': 'Photo added',
    'photo_deleted': 'Photo deleted',
    'part_added': 'Part added',
    'note_added': 'Note added',
    'document_added': 'Document added',
    'document_deleted': 'Document deleted',
    'milestone_completed': 'Milestone completed',
    'milestone_added': 'Milestone added'
  };
  return labels[action] || action;
};

export default TaskDetailPage;
